<template>
    <meta name="csrf-token" content="{{ csrf_token() }}">
    <h1
        class="flex flex-row flex-wrap content-center mb-3 text-sm font-bold text-gray-400"
    >
        <span class="flex items-center w-full mt-2"> DOCUMENTOS</span>
    </h1>
    <empty-state
        title="Nenhum documento pessoal encontrado"
        message="Este usuário ainda não possui documentos registrados."
        v-if="!loading && documents && documents.length === 0"
    />
    <div class="flex flex-col gap-4">
        <div
            class="flex items-center justify-between p-4 rounded-md shadow dark:bg-gray-800"
            v-for="document in documents"
            v-bind:key="document.id"
        >
            <div class="flex items-center gap-4">
                <div
                    class="flex items-center justify-center w-8 h-8 rounded-md dark:text-white"
                >
                    <DocumentIcon class="flex-shrink-0 w-5 h-5 text-gray-400" />
                </div>
                <p class="font-medium dark:text-white">
                    {{ formatDocumentName(document.collection_name) }}
                </p>
            </div>
            <div class="flex items-center gap-2">
                <div
                    v-if="document.status == 'approved'"
                    class="flex items-center justify-center p-4 mr-4 bg-white rounded-lg shadow-md"
                >
                    <div class="flex items-center justify-center mr-10">
                        <div
                            class="flex items-center justify-center w-10 h-10 bg-green-500 rounded-full"
                        >
                            <CheckIcon
                                class="flex-shrink-0 w-5 h-5 text-white text-gray-400"
                            />
                        </div>
                        <div class="ml-3">
                            <h3 class="text-lg font-bold text-gray-800">
                                Validado
                            </h3>
                            <p class="text-gray-600" v-if="shouldShowDocumentType(document.collection_name)">
                                Tipo:
                                {{
                                    (
                                        document.document_type ??
                                        "Não Informado"
                                    ).toUpperCase()
                                }}
                            </p>
                            <p class="text-gray-600" v-if="document.document_type == 'cnh'">
                                Número: {{ document.document_number ?? "Não Informado" }}
                            </p>
                            <p class="text-gray-600">
                                Por: {{ document.reviewed_by.name ?? '' }}
                            </p>
                            <p class="text-gray-600">
                                Em: {{ formatDate(document.reviewed_at) }}
                            </p>
                        </div>
                    </div>
                </div>

                <div
                    v-if="document.status == 'pending' && shouldShowDocumentType(document.collection_name)"
                    class="flex items-center px-4 py-2 mr-10 text-sm font-medium text-white border border-white rounded-full bg-white/10"
                >
                    <DocumentIcon
                        class="flex-shrink-0 w-5 h-5 mr-2 text-white"
                    />
                    <span>
                        Tipo:
                        {{
                            (
                                document.document_type ?? "Não Informado"
                            ).toUpperCase()
                        }}
                        <br>
                        <span v-if="document.document_type == 'cnh'">
                            Número: {{ document.document_number ?? "Não Informado" }}
                        </span>
                    </span>
                </div>

                <div
                    v-if="document.status == 'pending'"
                    class="flex items-center px-4 py-2 mr-10 text-sm font-medium text-yellow-700 bg-yellow-100 border border-yellow-300 rounded-full"
                >
                    <ExclamationCircleIcon
                        class="flex-shrink-0 w-5 h-5 mr-2 text-yellow-700"
                    />
                    <span>Pendente Aprovação</span>
                </div>

                <div
                    v-if="document.status == 'reproved'"
                    class="flex items-center justify-center p-4 mr-4 bg-white rounded-lg shadow-md"
                >
                    <div class="flex items-center justify-center mr-10">
                        <div
                            class="flex items-center justify-center w-10 h-10 bg-red-500 rounded-full"
                        >
                            <ExclamationTriangleIcon
                                class="flex-shrink-0 w-5 h-5 text-white text-gray-400"
                            />
                        </div>
                        <div class="ml-3">
                            <h3 class="text-lg font-bold text-gray-800">
                                Reprovado
                            </h3>
                            <p class="text-gray-600" v-if="shouldShowDocumentType(document.collection_name)">
                                Tipo:
                                {{
                                    (
                                        document.document_type ??
                                        "Não Informado"
                                    ).toUpperCase()
                                }}
                            </p>
                            <p class="text-gray-600" v-if="document.document_type == 'cnh'">
                                Número: {{ document.document_number ?? "Não Informado" }}
                            </p>
                            <p class="text-gray-600">
                                Por: {{ document.reviewed_by.name ?? '' }}
                            </p>
                            <p class="text-gray-600">
                                Em: {{ formatDate(document.reviewed_at) }}
                            </p>
                        </div>
                    </div>
                </div>

                <button
                    @click="showImage(document.temporary_url)"
                    class="flex items-center px-4 py-2 font-medium text-white bg-blue-500 rounded-md"
                >
                    <PhotoIcon class="w-5 h-5 mr-2 text-gray-400" />
                    Visualizar
                </button>

                <button
                    @click="openModal(document)"
                    class="flex items-center px-4 py-2 font-medium text-white bg-yellow-500 rounded-md"
                >
                    <PencilSquareIcon
                        class="flex-shrink-0 w-5 h-5 text-white text-gray-400"
                    />
                    Editar
                </button>
                <button
                    v-if="
                        document.status != 'approved'
                    "
                    @click="updateStatus(document.id, 'approved')"
                    class="flex items-center px-4 py-2 font-medium text-white bg-green-500 rounded-md"
                >
                    <CheckBadgeIcon
                        class="flex-shrink-0 w-5 h-5 text-white text-gray-400"
                    />
                    Validar
                </button>
                <button
                    v-if="
                        document.status !== 'reproved' &&
                        document.status !== 'approved'
                    "
                    @click="updateStatus(document.id, 'reproved')"
                    class="flex items-center px-4 py-2 font-medium text-white bg-red-500 rounded-md"
                >
                    <ExclamationTriangleIcon
                        class="flex-shrink-0 w-5 h-5 text-white text-gray-400"
                    />
                    Reprovar
                </button>
                <button
                    @click="deleteDocument(document.id)"
                    class="flex items-center px-4 py-2 font-medium text-white bg-red-500 rounded-md"
                >
                    <TrashIcon
                        class="flex-shrink-0 w-5 h-5 text-white text-gray-400"
                    />
                    Deletar
                </button>
            </div>
        </div>
    </div>

    <h1
        class="flex flex-row flex-wrap content-center mb-3 text-sm font-bold text-gray-400"
    >
        <span class="flex items-center w-full mt-2">
            DOCUMENTOS CONTA BANCÁRIA
        </span>
    </h1>
    <empty-state
        title="Nenhum documento bancário encontrado"
        message="Este usuário ainda não cadastrou nenhuma conta bancária."
        v-if="
            !loading &&
            documentsBankAccount &&
            documentsBankAccount.length === 0
        "
    />
    <div class="flex flex-col gap-4">
        <div
            class="flex items-center justify-between p-4 rounded-md shadow dark:bg-gray-800"
            v-for="document in documentsBankAccount"
            v-bind:key="document.id"
        >
            <div class="flex items-center gap-4">
                <div
                    class="flex items-center justify-center w-8 h-8 rounded-md dark:text-white"
                >
                    <DocumentIcon class="flex-shrink-0 w-5 h-5 text-gray-400" />
                </div>
                <p class="font-medium dark:text-white" v-if="shouldShowDocumentName(document.collection_name)">
                    {{ formatDocumentName(document.collection_name) }}
                </p>
            </div>
            <div class="flex items-center gap-2">
                <div
                    v-if="document.status == 'approved'"
                    class="flex items-center justify-center p-4 mr-4 bg-white rounded-lg shadow-md"
                >
                    <div class="flex items-center justify-center mr-10">
                        <div
                            class="flex items-center justify-center w-10 h-10 bg-green-500 rounded-full"
                        >
                            <CheckIcon
                                class="flex-shrink-0 w-5 h-5 text-white text-gray-400"
                            />
                        </div>
                        <div class="ml-3">
                            <h3 class="text-lg font-bold text-gray-800">
                                Validado
                            </h3>
                            <p class="text-gray-600" v-if="shouldShowDocumentType(document.collection_name)">
                                Tipo:
                                {{
                                    (
                                        document.document_type ??
                                        "Não Informado"
                                    ).toUpperCase()
                                }}
                            </p>
                            <p class="text-gray-600" v-if="document.document_type == 'cnh'">
                                Número: {{ document.document_number ?? "Não Informado" }}
                            </p>
                            <p class="text-gray-600">
                                Por: {{ document.reviewed_by.name ?? '' }}
                            </p>
                            <p class="text-gray-600">
                                Em: {{ formatDate(document.reviewed_at) }}
                            </p>
                        </div>
                    </div>
                </div>

                <div
                    v-if="document.status == 'pending'"
                    class="flex items-center px-4 py-2 mr-10 text-sm font-medium text-white border border-white rounded-full bg-white/10"
                >
                    <DocumentIcon
                        class="flex-shrink-0 w-5 h-5 mr-2 text-white"
                    />
                    <span v-if="shouldShowDocumentType(document.collection_name)">
                        Tipo:
                        {{
                            (
                                document.document_type ?? "Não Informado"
                            ).toUpperCase()
                        }}
                        <br>
                        <span v-if="document.document_type == 'cnh'">
                            Número: {{ document.document_number ?? "Não Informado" }}
                        </span>
                    </span>
                </div>

                <div
                    v-if="document.status == 'pending'"
                    class="flex items-center px-4 py-2 mr-10 text-sm font-medium text-yellow-700 bg-yellow-100 border border-yellow-300 rounded-full"
                >
                    <ExclamationCircleIcon
                        class="flex-shrink-0 w-5 h-5 mr-2 text-yellow-700"
                    />
                    <span>Pendente Aprovação</span>
                </div>

                <div
                    v-if="document.status == 'reproved'"
                    class="flex items-center justify-center p-4 mr-4 bg-white rounded-lg shadow-md"
                >
                    <div class="flex items-center justify-center mr-10">
                        <div
                            class="flex items-center justify-center w-10 h-10 bg-red-500 rounded-full"
                        >
                            <ExclamationTriangleIcon
                                class="flex-shrink-0 w-5 h-5 text-white text-gray-400"
                            />
                        </div>
                        <div class="ml-3">
                            <h3 class="text-lg font-bold text-gray-800">
                                Reprovado
                            </h3>
                            <p class="text-gray-600" v-if="shouldShowDocumentType(document.collection_name)">
                                Tipo:
                                {{
                                    (
                                        document.document_type ??
                                        "Não Informado"
                                    ).toUpperCase()
                                }}
                            </p>
                            <p class="text-gray-600" v-if="document.document_type == 'cnh'">
                                Número: {{ document.document_number ?? "Não Informado" }}
                            </p>
                            <p class="text-gray-600">
                                Por: {{ document.reviewed_by.name ?? '' }}
                            </p>
                            <p class="text-gray-600">
                                Em: {{ formatDate(document.reviewed_at) }}
                            </p>
                        </div>
                    </div>
                </div>

                <button
                    @click="showImage(document.temporary_url)"
                    class="flex items-center px-4 py-2 font-medium text-white bg-blue-500 rounded-md"
                >
                    <PhotoIcon class="w-5 h-5 mr-2 text-gray-400" />
                    Visualizar
                </button>
            </div>
        </div>
    </div>

    <modal class="4xl" :open="show" @close="show = false">
        <div
            class="flex flex-col flex-wrap w-full space-y-3 text-white divide-y min-h-fit divide-slate-500"
        ></div>
        <h1
            class="flex flex-row flex-wrap items-center content-center pb-1 mb-2 text-sm text-lg font-bold text-white border-b border-slate-500"
        >
            Alterar Documento -
            {{ formatDocumentName(this.document.collection_name) }}
        </h1>

        <input
            class="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-gray-400 dark:placeholder-gray-400"
            id="file_input"
            type="file"
            ref="file"
        />

        <label
            for="type"
            class="block mt-4 text-sm font-medium text-gray-700 dark:text-white"
        >
            Tipo de Documento:
        </label>
        <select
            v-model="this.document.document_type"
            id="document_type"
            class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
        >
            <option value="cnh">CNH</option>
            <option value="rg">RG</option>
        </select>


        <div v-if="this.document.document_type == 'cnh'">
            <label
                for="document_number"
                class="block mt-4 text-sm font-medium text-gray-700 dark:text-white"
        >
            Número do Documento:
        </label>
        <input
            v-model="this.document.document_number"
            id="document_number"
                class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
            />
        </div>

        <div class="flex justify-center mt-3">
            <button
                :disabled="loading"
                type="button"
                @click.prevent="sendDocument"
                :loading="loading"
                class="inline-flex justify-center w-full max-w-xs px-4 py-2 mb-3 text-base font-medium text-white bg-yellow-500 border border-transparent rounded-md shadow-sm focus:outline-none sm:w-auto sm:text-sm"
            >
                <PencilSquareIcon
                    class="flex-shrink-0 w-5 h-5 text-white text-gray-400"
                />
                Editar
            </button>
        </div>
    </modal>

    <modal class="4xl" :open="decisionModal" @close="decisionModal = false">
        <div
            class="flex flex-col flex-wrap w-full space-y-3 text-white divide-y min-h-fit divide-slate-500"
        ></div>
        <h1
            class="flex flex-row flex-wrap items-center content-center pb-1 mb-2 text-sm text-lg font-bold text-white border-b border-slate-500"
        >
            Validação
        </h1>

        <div v-if="decision != 'approved'">
            <p class="p-4 text-white bg-blue-600 rounded-md shadow-md">
                Caso deseje, você pode informar um motivo no campo abaixo. Se
                preferir não preencher, será utilizada uma mensagem padrão para
                o usuário.
            </p>

            <ReasonSelect
                reasonType="user"
                @reason-selected="handleReasonSelected"
            />
        </div>

        <p class="mt-4 text-white">
            Você tem certeza que deseja
            {{ decision == "approved" ? "validar" : "reprovar" }} este
            documento?
        </p>

        <div class="flex justify-center mt-3 space-x-3">
            <button
                :disabled="loading"
                type="button"
                @click.prevent="updateStatusFetch(decisionDocumment, decision)"
                :loading="loading"
                class="inline-flex justify-center w-full max-w-xs px-4 py-2 mb-3 text-base font-medium text-white bg-green-500 border border-transparent rounded-md shadow-sm focus:outline-none sm:w-auto sm:text-sm"
            >
                <CheckIcon
                    class="flex-shrink-0 w-5 h-5 text-white text-gray-400"
                />
                Sim
            </button>
            <button
                type="button"
                @click.prevent="decisionModal = false"
                class="inline-flex justify-center w-full max-w-xs px-4 py-2 mb-3 text-base font-medium text-white bg-red-500 border border-transparent rounded-md shadow-sm focus:outline-none sm:w-auto sm:text-sm"
            >
                <ExclamationTriangleIcon
                    class="flex-shrink-0 w-5 h-5 text-white text-gray-400"
                />
                Não
            </button>
        </div>
    </modal>
    <vue-easy-lightbox
        :visible="vueEasy"
        :imgs="urlDocument"
        :index="0"
        @hide="handleHide"
    />
    <modal
        class="min-h-full"
        size="4xl"
        :open="showDocumentModal"
        @close="showDocumentModal = false"
    >
        <div
            class="flex flex-col w-full min-h-full space-y-3 text-white divide-y divide-slate-500"
            style="height: 1000px"
        >
            <h1
                class="pb-1 mb-2 text-lg font-bold text-white border-b border-slate-500"
            >
                Documento
            </h1>

            <div class="flex items-stretch flex-grow h-full">
                <iframe
                    :src="urlDocument"
                    class="w-full h-full"
                    frameborder="0"
                    allowfullscreen
                ></iframe>
            </div>

            <div class="flex justify-center mt-5 space-x-3">
                <button
                    type="button"
                    @click.prevent="showDocumentModal = false"
                    class="inline-flex justify-center w-full max-w-xs px-4 py-2 mb-3 text-base font-medium text-white bg-yellow-500 border border-transparent rounded-md shadow-sm focus:outline-none sm:w-auto sm:text-sm"
                >
                    Fechar
                </button>
            </div>
        </div>
    </modal>
</template>

<script>
import {
    ArrowDownCircleIcon,
    CheckBadgeIcon,
    CheckIcon,
    DocumentIcon,
    ExclamationCircleIcon,
    ExclamationTriangleIcon,
    PaperClipIcon,
    PencilSquareIcon,
    PhotoIcon,
    TrashIcon,
} from "@heroicons/vue/24/outline";
import dayjs from "dayjs";
import VueEasyLightbox from "vue-easy-lightbox";
import { useToast } from "vue-toastification";
import ReasonSelect from "../../components/layouts/ReasonSelect.vue";
import HelperMixin from "../../mixins/HelperMixin.vue";
import EmptyState from "../layouts/EmptyState.vue";
import Modal from "../layouts/Modal.vue";
import UpdateDocuments from "./UpdateDocuments.vue";

export default {
    components: {
        UpdateDocuments,
        PaperClipIcon,
        EmptyState,
        ArrowDownCircleIcon,
        CheckBadgeIcon,
        CheckIcon,
        PencilSquareIcon,
        DocumentIcon,
        ExclamationCircleIcon,
        DocumentIcon,
        ExclamationTriangleIcon,
        TrashIcon,
        Modal,
        PhotoIcon,
        VueEasyLightbox,
        ReasonSelect,
    },
    props: {
        user: { type: Object, required: true },
        statuses: { String, required: true },
    },
    mixins: [HelperMixin],
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            loading: false,
            documentsBankAccount: [],
            documents: [],
            show: false,
            decisionModal: false,
            decision: null,
            image_url: null,
            document: null,
            decisionDocumment: null,
            showDocumentModal: false,
            urlDocument: null,
            vueEasy: false,
            reason: null,
        };
    },

    mounted() {
        this.fetch(false);
    },

    methods: {
        async showImage(index) {
            const response = await fetch(index, { method: "GET" });
            const contentType = response.headers.get("Content-Type");

            if (contentType.includes("image")) {
                this.index = index;
                this.urlDocument = index;
                this.vueEasy = true;
            } else {
                this.urlDocument = index;
                this.showDocumentModal = true;
            }
        },
        handleHide() {
            this.vueEasy = false;
        },
        openModal(document) {
            this.show = true;
            this.document = document;
        },
        formatDate(date) {
            return dayjs(date).format("DD/MM/YYYY HH:mm:ss");
        },
        fetchStatus() {
            axios
                .get("/api/user/" + this.user.id + "/status")
                .then(({ data }) => {
                    this.$emit("update-statuses", data);
                    this.loading = false;
                })
                .catch((error) => {
                    this.loading = false;
                    this.toast.error(error.response.data.message);
                });
        },
        updateStatus(document, decision) {
            this.decisionModal = true;
            this.decision = decision;
            this.decisionDocumment = document;
        },
        updateStatusFetch(document, decision) {
            const route =
                "/api/user/" +
                this.user.id +
                "/documents/" +
                document +
                "/status";
            axios
                .put(route, { decision: decision, reason: this.reason })
                .then(({ data }) => {
                    this.toast.success("Salvo com sucesso.");
                    this.fetch(false);
                    this.fetchStatus();
                    this.show = false;
                    this.decisionModal = false;
                    this.decision = null;
                    this.decisionDocumment = null;
                })
                .catch((error) => {});
        },
        handleReasonSelected(reason) {
            this.reason = reason;
        },
        fetch(update = true) {
            this.documents = [];
            this.documentsBankAccount = [];
            this.loading = true;

            if (update) {
                this.documents = [];
            }

            axios
                .get("/api/user/" + this.user.id + "/documents")
                .then(({ data }) => {
                    this.documents = [...data, ...this.documents];
                })
                .catch((error) => {});

            if (this.user.bank_accounts.at(-1)?.id !== undefined) {
                axios
                    .get(
                        "/api/bank-account/" +
                            this.user.bank_accounts.at(-1)?.id +
                            "/documents",
                    )
                    .then(({ data }) => {
                        this.documentsBankAccount = [
                            ...data,
                            ...this.documentsBankAccount,
                        ];
                    })
                    .catch((error) => {});
            }

            this.loading = false;
        },
        openLink(url) {
            this.showDocumentModal = true;
            this.urlDocument = url;
            //window.open(url, "_blank");
        },

        sendDocument() {
            if (!this.$refs.file.files[0]) {
                const route =
                    "/api/user/" +
                    this.user.id +
                    "/documents/" +
                    this.document.id +
                    "/type";
                axios
                    .put(route,
                        { type: this.document.document_type, document_number: this.document.document_number ?? null },
                        {
                            headers: {
                                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                            }
                        }
                    )
                    .then(({ data }) => {
                        this.toast.success("Salvo com sucesso.");
                        this.fetch(false);
                        this.fetchStatus();
                        this.show = false;
                        this.decisionModal = false;
                        this.decision = null;
                        this.decisionDocumment = null;
                    })
                    .catch((error) => {
                        this.toast.error("Erro ao atualizar documento!");
                    });
                return;
            }

            this.loading = true;

            Vapor.store(this.$refs.file.files[0], {
                progress: (progress) => {
                    this.progress = Math.round(progress * 100);
                },
            }).then((response) => {
                axios
                    .put("/api/user/" + this.user.id + "/documents",
                        {
                            uuid: response.uuid,
                            key: response.key,
                            bucket: response.bucket,
                            name: this.$refs.file.files[0].name,
                            content_type: this.$refs.file.files[0].type,
                            collection: this.document.collection_name,
                            type: this.document.document_type,
                            document_number: this.document.document_number ?? null,
                        },
                        {
                            headers: {
                                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
                            }
                        }
                    )
                    .then((response) => {
                        this.toast.success("Documento atualizado com sucesso!");
                    })
                    .catch((error) => {
                        this.toast.error("Erro ao atualizar documento!");
                    })
                    .finally(() => {
                        this.loading = false;
                        this.update = false;
                        this.fetch(false);
                        this.show = false;
                    });
            });
        },

        deleteDocument(document) {
            axios
                .delete("/api/user/" + this.user.id + "/documents/" + document)
                .then((response) => {
                    this.toast.success("Documento deletado com sucesso!");
                    this.fetch(false);
                })
                .catch((error) => {
                    this.toast.error("Erro ao deletar documento!");
                });
        },
        shouldShowDocumentType(collectionName) {
            const documentsWithType = [
                'documentFront',
                'document_front',
                'documentBack',
                'document_back',
                'selfiePhoto',
                'selfie_photo',
                'bankDocument',
                'bank_document'
            ];
            return documentsWithType.includes(collectionName);
        },
        shouldShowDocumentName(collectionName) {
            const documentsWithName = [
                'documentFront',
                'document_front',
                'documentBack',
                'document_back',
                'selfiePhoto',
                'selfie_photo',
                'bankDocument',
                'bank_document'
            ];
            return documentsWithName.includes(collectionName);
        },
    },
};
</script>

<style>
.image-gallery {
    display: flex;
    gap: 10px;
}

.thumbnail {
    width: 100px;
    height: auto;
    cursor: pointer;
    transition: transform 0.2s;
}

.thumbnail:hover {
    transform: scale(1.1);
}
</style>
